import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import AuctionCard from "@/components/AuctionCard";
import { AuctionDisclaimer } from "@/pages/AuctionDetail";
import { api } from "@/lib/api";
import { CITIES_ALL } from "@/lib/brand";
import { Gavel, ShieldCheck, FileSearch, Handshake } from "lucide-react";

const STATUS_TABS = [
    { k: "upcoming", l: "Upcoming" },
    { k: "closed",   l: "Closed" },
    { k: "all",      l: "All" },
];
const TYPES = ["All Types", "Apartment", "Villa", "Plot", "Land", "Commercial"];
const STEPS = [
    { Icon: FileSearch,  t: "Notice review",   d: "We read the official sale notice and flag dues, possession type and conditions." },
    { Icon: ShieldCheck, t: "Legal diligence", d: "Title, encumbrance and society/authority checks before you commit the EMD." },
    { Icon: Handshake,   t: "Bid assistance",  d: "Registration on the e-auction portal, EMD and documentation support." },
];

export default function AuctionProperties() {
    const [params] = useSearchParams();
    const [items, setItems] = useState(null);
    const [status, setStatus] = useState("upcoming");
    const [city, setCity] = useState(params.get("city") || "All Cities");
    const [type, setType] = useState(params.get("type") || "All Types");

    useEffect(() => {
        api.get("/properties", { params: { listing_type: "auction", limit: 200 } })
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]));
    }, []);

    const shown = useMemo(() => (items || [])
        .filter((x) => status === "all" || x.auction_status === status)
        .filter((x) => city === "All Cities" || x.city === city)
        .filter((x) => type === "All Types" || x.type === type)
        .sort((a, b) => (a.auction_status === b.auction_status ? 0 : a.auction_status === "upcoming" ? -1 : 1)
            || String(a.auction_date || "").localeCompare(String(b.auction_date || "")) * (a.auction_status === "closed" ? -1 : 1)),
    [items, status, city, type]);

    return (
        <PageShell navVariant="dark">
            <section className="bg-[#0a0d18] text-white pt-32 pb-16" data-testid="auctions-hero">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]"><Gavel size={13}/> Auction Properties</div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">Bank &amp; authority auctions,<br/><em className="not-italic text-[var(--ebg-gold)]">read before you bid.</em></h1>
                        <p className="text-white/70 mt-5 max-w-xl leading-relaxed text-sm md:text-base">Curated auction listings from public notices of banks, tribunals and development authorities — with EBG&rsquo;s legal desk to help you verify, inspect and bid.</p>
                    </div>
                    <div className="lg:col-span-5 grid gap-3">
                        {STEPS.map(({ Icon, t, d }) => (
                            <div key={t} className="flex items-start gap-3 border border-white/10 bg-white/[0.03] p-4">
                                <Icon size={16} className="text-[var(--ebg-gold)] mt-0.5 shrink-0"/>
                                <div><div className="text-[12px] uppercase tracking-[0.18em] font-bold">{t}</div><div className="text-white/60 text-[13px] mt-1">{d}</div></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-12" data-testid="auctions-list">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
                    <div className="flex gap-1">
                        {STATUS_TABS.map((t) => (
                            <button key={t.k} onClick={() => setStatus(t.k)} data-testid={`auction-filter-${t.k}`}
                                className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors ${status === t.k ? "bg-black text-white" : "border border-black/15 text-black/60 hover:text-black hover:border-black"}`}>
                                {t.l}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-3">
                        <FilterSelect value={city} onChange={setCity} opts={["All Cities", ...CITIES_ALL]} testid="auction-filter-city"/>
                        <FilterSelect value={type} onChange={setType} opts={TYPES} testid="auction-filter-type"/>
                    </div>
                </div>

                {items === null ? (
                    <div className="text-black/40 py-20 text-center">Loading auctions…</div>
                ) : shown.length === 0 ? (
                    <div className="border border-dashed border-black/15 py-20 text-center text-black/55" data-testid="auctions-empty">No {status !== "all" ? status : ""} auctions match these filters right now.</div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {shown.map((p) => <AuctionCard key={p.id} item={p}/>)}
                    </div>
                )}

                <div className="mt-14"><AuctionDisclaimer/></div>
            </section>
        </PageShell>
    );
}

function FilterSelect({ value, onChange, opts, testid }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="border border-black/15 bg-white px-4 py-2.5 text-[12px] font-medium outline-none focus:border-[var(--ebg-gold)]">
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}
